import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, AlertCircle, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import type {
  ClientsListResponse,
  GenerateDocumentsRequest,
  GenerateDocumentsResponse,
  DocumentGenerationResult,
} from '@common/types'
import { DRATA_UPLOAD_ERROR_LABELS } from '@common/utils/drataErrors'
import { api } from '@frontend/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@frontend/components/ui/table'
import { MultiSelect } from '@frontend/components/ui/multi-select'

interface GenerateDocumentsDialogProps {
  reviewId: number
  vendorName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

async function fetchClients(): Promise<ClientsListResponse> {
  return api.get('clients/minimal').json<ClientsListResponse>()
}

async function generateDocuments(
  reviewId: number,
  request: GenerateDocumentsRequest
): Promise<GenerateDocumentsResponse> {
  return api.post(`reviews/${reviewId}/generate-documents`, { json: request }).json<GenerateDocumentsResponse>()
}

type DialogState = 'form' | 'generating' | 'results'

export function GenerateDocumentsDialog({ reviewId, vendorName, open, onOpenChange }: GenerateDocumentsDialogProps) {
  const queryClient = useQueryClient()

  // Form state
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [manualClientNames, setManualClientNames] = useState<string[]>([])
  const [uploadToDrata, setUploadToDrata] = useState(true)

  // Dialog state
  const [dialogState, setDialogState] = useState<DialogState>('form')
  const [results, setResults] = useState<GenerateDocumentsResponse | null>(null)

  // Fetch clients for the dropdown
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
    enabled: open,
  })

  const generateMutation = useMutation({
    mutationFn: (request: GenerateDocumentsRequest) => generateDocuments(reviewId, request),
    onMutate: () => {
      setDialogState('generating')
    },
    onSuccess: (data) => {
      setResults(data)
      setDialogState('results')
      // Refresh documents list
      queryClient.invalidateQueries({ queryKey: ['review', String(reviewId), 'documents'] })
    },
    onError: () => {
      setDialogState('form')
    },
  })

  const clients = clientsData?.clients || []

  const clientOptions = clients.map((client) => ({
    label: client.name,
    value: client.id,
  }))

  const handleAddManualClient = (name: string) => {
    if (name && !manualClientNames.includes(name)) {
      setManualClientNames((prev) => [...prev, name])
    }
  }

  const handleRemoveManualClient = (name: string) => {
    setManualClientNames((prev) => prev.filter((n) => n !== name))
  }

  const handleGenerate = () => {
    const clientsToGenerate: GenerateDocumentsRequest['clients'] = [
      ...selectedClientIds.map((id) => {
        const client = clients.find((c) => c.id === id)
        return { clientId: id, clientName: client?.name || '' }
      }),
      ...manualClientNames.map((name) => ({ clientName: name })),
    ]

    if (clientsToGenerate.length === 0) return

    generateMutation.mutate({
      clients: clientsToGenerate,
      uploadToDrata,
    })
  }

  const handleClose = () => {
    // Reset state when closing
    setDialogState('form')
    setSelectedClientIds([])
    setManualClientNames([])
    setUploadToDrata(true)
    setResults(null)
    onOpenChange(false)
  }

  const totalSelected = selectedClientIds.length + manualClientNames.length

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Documents
          </DialogTitle>
          <DialogDescription>Generate compliance documents for {vendorName}</DialogDescription>
        </DialogHeader>

        {dialogState === 'form' && (
          <>
            {/* Client Selection */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Select Clients</label>
                <p className="text-xs text-muted-foreground mb-2">Choose existing clients or type to create new ones</p>
                {clientsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <MultiSelect
                    options={clientOptions}
                    selected={selectedClientIds}
                    onChange={setSelectedClientIds}
                    placeholder="Search and select clients..."
                    searchPlaceholder="Search or type to create..."
                    emptyMessage="No clients found."
                    allowCreate
                    onCreateItem={handleAddManualClient}
                    manualItems={manualClientNames}
                    onRemoveManualItem={handleRemoveManualClient}
                  />
                )}
              </div>

              {/* Upload to Drata checkbox */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={uploadToDrata}
                    onChange={(e) => setUploadToDrata(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Upload to Drata</span>
                </label>
                <p className="text-xs text-muted-foreground ml-6">
                  Automatically upload generated PDFs to Drata for clients with API keys
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={totalSelected === 0}>
                Generate for {totalSelected} client{totalSelected !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </>
        )}

        {dialogState === 'generating' && (
          <div className="py-8 text-center">
            <Spinner size="lg" className="mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">
              Generating documents for {totalSelected} client{totalSelected !== 1 ? 's' : ''}...
            </p>
            <p className="text-xs text-muted-foreground mt-2">This may take a few moments</p>
          </div>
        )}

        {dialogState === 'results' && results && (
          <>
            {/* Summary */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span className="text-sm font-medium">{results.summary.succeeded} succeeded</span>
              </div>
              {results.summary.failed > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium">{results.summary.failed} failed</span>
                </div>
              )}
            </div>

            {/* Non-fatal warnings (e.g. bridge-letter co-upload problems) */}
            {results.warnings && results.warnings.length > 0 && (
              <div className="flex items-start gap-3 rounded-md bg-amber-500/10 px-4 py-3 text-amber-700">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  {results.warnings.map((warning, i) => (
                    <p key={i}>{warning}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Results Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Drata</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.results.map((result, i) => (
                  <ResultRow key={i} result={result} />
                ))}
              </TableBody>
            </Table>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}

        {/* Error state */}
        {generateMutation.isError && dialogState === 'form' && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4" />
            Failed to generate documents. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ResultRow({ result }: { result: DocumentGenerationResult }) {
  const statusIcon =
    result.status === 'success' ? (
      <CheckCircle className="h-4 w-4 text-emerald-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    )

  const drataStatus = () => {
    if (result.status === 'failed') {
      return <span className="text-muted-foreground">-</span>
    }
    if (!result.drataRequested) {
      return <span className="text-muted-foreground text-xs">Not requested</span>
    }
    if (!result.clientHasDrataKey) {
      return <span className="text-muted-foreground text-xs">No API key</span>
    }
    if (result.drataUploaded) {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
          <CheckCircle className="h-3 w-3" />
          Uploaded
        </span>
      )
    }
    // Failed to upload
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-xs">
        <AlertCircle className="h-3 w-3" />
        {result.drataError ? DRATA_UPLOAD_ERROR_LABELS[result.drataError] : 'Failed'}
      </span>
    )
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          {statusIcon}
          <span className="font-medium">{result.clientName}</span>
        </div>
        {result.error && <p className="text-xs text-red-600 mt-1">{result.error}</p>}
      </TableCell>
      <TableCell>
        {result.googleDriveId ? (
          <a
            href={`https://docs.google.com/document/d/${result.googleDriveId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline text-sm"
          >
            View Doc
          </a>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>{drataStatus()}</TableCell>
    </TableRow>
  )
}
